import React, { useEffect, useState } from "react";
import { connect, useSelector } from "react-redux";
import clsx from "clsx";
import { makeStyles, styled } from "@mui/styles";
import { useLocation } from "react-router-dom";
import {
	Box,
	Collapse,
	Drawer,
	IconButton,
	List,
	ListItem,
	ListItemText,
	Tooltip,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

import TopBar from "../components/TopBar";
import {
	PATH_CODE_SETTING,
	SLUG_ASSET_SENTINEL,
	SLUG_TRAINING,
} from "../utils/routeConstants";
import menuSettings from "../config/configureMenu";
import { getAccessPermission } from "../reducers/PermissionsReducer";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const TOPBAR_HEIGHT = 70;
const DRAWER_WIDTH = 300;
const COLLAPSED_WIDTH = 56;

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		flexDirection: "column",
		backgroundColor: theme.palette.bg,
	},
	menu: {
		width: "100%",
		"& hr": {
			marginTop: "0.5rem",
			marginBottom: "0.5rem",
		},
	},
	link: {
		"& > span": {
			fontSize: 16,
			fontWeight: 500,
			color: theme.palette.text.primary,
		},
	},
	activeLink: {
		"& > span": {
			fontSize: 16,
			color: theme.palette.topBar.submenu,
			fontWeight: 900,
		},
	},
	hasSubmenu: {
		marginTop: "5px",
		cursor: "pointer",
		"&:hover": {
			background: theme.colors.grey5,
		},
	},
	submenu: {
		"& > $link": {
			"& span": {
				fontWeight: 400,
				fontSize: 16,
				paddingLeft: 15,
				lineHeight: 1,
			},
		},
		"& > $hasSubmenu": {
			"& span": {
				fontWeight: 500,
				fontSize: 16,
				paddingLeft: 15,
				lineHeight: 1,
			},
		},
		"& > $submenu": {
			"& > $link": {
				"& span": {
					fontWeight: 600,
					fontSize: 16,
					paddingLeft: 30,
					lineHeight: 1,
					//fontSize: '0.9em',
				},
			},
		},
	},
	active: {
		color: theme.palette.secondary.main,
	},
	body: {
		display: "flex",
		flex: 1,
		marginTop: TOPBAR_HEIGHT,
		backgroundColor: theme.palette.bg,
	},

	content: {
		flexGrow: 1,
		backgroundColor: theme.palette.mode === "dark" ? "#000" : "#fff",
		borderRadius: "8px",
		marginTop: "25px",
		marginLeft: "20px",
		paddingLeft: "15px",
		overflow: "auto",
		transition: theme.transitions.create("margin", {
			easing: theme.transitions.easing.easeOut,
			duration: theme.transitions.duration.enteringScreen,
		}),
	},

	contentInner: {
		padding: "16px",
		minHeight: "calc(100vh - 70px)",
		boxSizing: "border-box",
	},
	noPadding: {
		paddingLeft: 0,
	},
	sideMenuTitle: {
		width: "100%",
		display: "flex",
		justifyContent: "center",
		fontSize: 20,
		fontWeight: 700,
		color: theme.palette.text.primary,
	},
}));

const DrawerHeader = styled("div")(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "flex-end",
	padding: theme.spacing(1),
}));

const SubAppLayout = (props) => {
	const { children, contentBackground } = props;

	const classes = useStyles();
	const theme = useTheme();
	const location = useLocation();
	const history = useHistory();
	const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
	const userAgency = useSelector((s) => s.user.userAgency);
	const permissions = useSelector((state) => state.permissions);

	const isCodeSettings = location.pathname.startsWith(PATH_CODE_SETTING);

	const [open, setOpen] = useState(true);
	const [sideMenuTitle, setSideMenuTitle] = useState("");
	const [sideMenuList, setSideMenuList] = useState([]);
	const [openSubMenu, setOpenSubMenu] = useState({});

	const toggleDrawer = () => setOpen((prev) => !prev);

	useEffect(() => {
		if (location.pathname.includes(SLUG_TRAINING)) {
			setSideMenuTitle("Training");
		} else if (location.pathname.includes(`${SLUG_ASSET_SENTINEL}/fleet`)) {
			setSideMenuTitle("Fleet");
		} else if (
			location.pathname.includes(`${SLUG_ASSET_SENTINEL}/equipment`)
		) {
			setSideMenuTitle("Equipment");
		}
	}, [location.pathname]);

	useEffect(() => {
		if (sideMenuTitle && permissions) {
			const module = menuSettings.find((m) => m.label === sideMenuTitle);
			const parentAllowed = hasRead(module);
			const filteredSub = module?.subMenu?.filter((s) => hasRead(s));

			setSideMenuList(parentAllowed ? filteredSub || [] : []);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sideMenuTitle, permissions]);

	const handleItemClick = (item) => history.push(item.link);

	const hasRead = (item) => {
		if (item?.alwaysVisible) return true;

		if (
			!item?.permission ||
			!item?.permission?.area ||
			!item?.permission?.key
		) {
			return false;
		}

		const permObj = getAccessPermission(
			item.permission.area,
			item.permission.key,
			userAgency,
		);

		return !!permObj?.Read;
	};

	const handleToggleClick = (item) => {
		setOpenSubMenu((prev) => ({
			...prev,
			[item.label]: !prev[item.label],
		}));
	};

	const renderMenu = (item, index) => {
		const { label, tooltip, link, subMenu } = item;
		const isActive = location.pathname === link;
		const isOpen = openSubMenu[label];

		return (
			<React.Fragment key={index}>
				<Tooltip
					title={tooltip}
					key={index}
					style={{ color: theme.palette.bgElements }}
				>
					<ListItem
						onClick={() =>
							subMenu
								? handleToggleClick(item)
								: handleItemClick(item)
						}
						className={classes.hasSubmenu}
					>
						<FontAwesomeIcon
							icon={item.icon}
							className="font-size-xl mr-2"
							style={{ color: theme.palette.text.primary }}
						/>
						<ListItemText
							primary={label}
							className={
								isActive ? classes.activeLink : classes.link
							}
						/>
						{subMenu && (isOpen ? <ExpandLess /> : <ExpandMore />)}
					</ListItem>
				</Tooltip>
				{subMenu && (
					<Collapse in={isOpen} timeout="auto" unmountOnExit>
						{subMenu.map((subItem, i) => (
							<Tooltip
								title={subItem.tooltip}
								key={i}
								style={{ color: theme.palette.bgElements }}
							>
								<ListItem
									key={i}
									onClick={(e) => {
										handleItemClick(subItem);
									}}
									style={{ paddingLeft: 40 }}
									className={classes.hasSubmenu}
								>
									<ListItemText
										primary={subItem.label}
										className={
											isActive
												? classes.activeLink
												: classes.link
										}
									/>
								</ListItem>
							</Tooltip>
						))}
					</Collapse>
				)}
			</React.Fragment>
		);
	};

	return (
		<div className={clsx(classes.root, contentBackground)}>
			<TopBar />
			<div className={classes.body}>
				{!isMobile && (
					<Drawer
						variant="permanent"
						anchor="left"
						sx={{
							width: open ? DRAWER_WIDTH : COLLAPSED_WIDTH,
							flexShrink: 0,
							paddingTop: "16px",
							marginTop: "8px",
							backgroundColor: theme.palette.bg,
							height: `calc(100vh - ${TOPBAR_HEIGHT + 27}px)`,
							"& .MuiDrawer-paper": {
								position: "relative",
								width: open ? DRAWER_WIDTH : COLLAPSED_WIDTH,
								height: `calc(100vh - ${TOPBAR_HEIGHT + 27}px)`,
								overflowX: "hidden",
								transition: theme.transitions.create("width", {
									easing: theme.transitions.easing.sharp,
									duration:
										theme.transitions.duration
											.enteringScreen,
								}),
								boxSizing: "border-box",
								borderTopRightRadius: 8,
								borderBottomRightRadius: 8,
							},
						}}
					>
						<DrawerHeader>
							{open && (
								<span className={classes.sideMenuTitle}>
									{sideMenuTitle}
								</span>
							)}
							<IconButton onClick={toggleDrawer}>
								{open ? (
									<ChevronLeftIcon />
								) : (
									<ChevronRightIcon />
								)}
							</IconButton>
						</DrawerHeader>

						<Box sx={{ px: 1 }}>
							<div className={classes.menu}>
								<List
									component="nav"
									aria-labelledby="nested-list-subheader"
									dense
									style={{
										display: "flex",
										flexDirection: "column",
									}}
								>
									{open &&
										sideMenuList.map((item, index) =>
											renderMenu(item, index),
										)}
								</List>
							</div>
						</Box>
					</Drawer>
				)}

				<div
					className={clsx(classes.content, {
						[classes.contentExpanded]: open,
					})}
					style={{
						height: `calc(100vh - ${TOPBAR_HEIGHT + 27}px)`,
					}}
				>
					<div
						className={clsx(
							classes.contentInner,
							isCodeSettings && classes.noPadding,
						)}
					>
						{children}
					</div>
				</div>
			</div>
		</div>
	);
};

const mapStateToProps = (state) => ({
	headerFixed: state.theme.headerFixed,
	headerDrawerToggle: state.theme.headerDrawerToggle,
	contentBackground: state.theme.contentBackground,
});

export default connect(mapStateToProps)(SubAppLayout);
